import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { fmtCurrency } from "../lib/format";
import { TopBar } from "../components/TopBar";
import { TotalsCard } from "../components/TotalsCard";
import { PeriodBadge } from "../components/PeriodBadge";
import { WeekComparison } from "../components/WeekComparison";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
  DialogFooter,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Plus, Loader2, Building2, CheckCircle2, Search, ArrowUpDown } from "lucide-react";
import { toast } from "sonner";
import { cn } from "../lib/utils";

const SORT_OPTIONS = [
  { value: "name-asc", label: "Name (A → Z)" },
  { value: "name-desc", label: "Name (Z → A)" },
  { value: "balance-desc", label: "Balance (high → low)" },
  { value: "balance-asc", label: "Balance (low → high)" },
];

export default function BrandsPortalPage() {
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState(null);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const [creating, setCreating] = useState(false);
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("name-asc");

  const visibleBrands = useMemo(() => {
    const q = search.trim().toLowerCase();
    const filtered = q
      ? brands.filter((b) => (b.name || "").toLowerCase().includes(q))
      : [...brands];
    const byName = (a, b) =>
      (a.name || "").localeCompare(b.name || "", undefined, { sensitivity: "base" });
    filtered.sort((a, b) => {
      switch (sortBy) {
        case "name-desc":
          return byName(b, a);
        case "balance-desc":
          return (b.total ?? 0) - (a.total ?? 0) || byName(a, b);
        case "balance-asc":
          return (a.total ?? 0) - (b.total ?? 0) || byName(a, b);
        case "name-asc":
        default:
          return byName(a, b);
      }
    });
    return filtered;
  }, [brands, search, sortBy]);

  const load = useCallback(async () => {
    try {
      const [dashRes, listRes] = await Promise.all([
        api.get("/brands/dashboard"),
        api.get("/brands"),
      ]);
      setDashboard(dashRes.data);
      setBrands(listRes.data);
    } catch (e) {
      toast.error("Failed to load brands data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const createBrand = async () => {
    if (!newName.trim()) return;
    setCreating(true);
    try {
      const res = await api.post("/brands", { name: newName.trim() });
      setDialogOpen(false);
      setNewName("");
      navigate(`/brands/${res.data.id}`);
    } catch (e) {
      toast.error("Failed to create brand");
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="min-h-screen bg-muted/30">
      <TopBar crumbs={[{ label: "Brands" }]} />
      <main className="mx-auto max-w-7xl space-y-6 px-4 py-8 sm:px-6 lg:px-8">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight sm:text-3xl">Brands Dashboard</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              Aggregated from every brand's current Total balance.
            </p>
          </div>
          <PeriodBadge />
        </div>

        {loading ? (
          <div className="flex h-40 items-center justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              <TotalsCard
                label="Total Available Balance"
                value={dashboard?.total_available_balance ?? 0}
                helper={`Across ${dashboard?.brands_count ?? 0} brand${(dashboard?.brands_count ?? 0) === 1 ? "" : "s"}`}
                testId="brands-total-available-balance"
                accent
              />
            </div>

            <WeekComparison
              testId="brands-week-comparison"
              lastWeek={dashboard?.last_week}
              rows={
                dashboard?.last_week
                  ? [
                      {
                        label: "Expenses",
                        current: dashboard.current_expenses ?? 0,
                        last: dashboard.last_week.expenses ?? 0,
                        invert: true,
                      },
                      {
                        label: "Total Available",
                        current: dashboard.total_available_balance ?? 0,
                        last: dashboard.last_week.total ?? 0,
                      },
                    ]
                  : []
              }
            />

            <Card className="shadow-sm">
              <CardHeader className="flex flex-col gap-3 space-y-0 pb-3 sm:flex-row sm:items-center sm:justify-between">
                <CardTitle className="text-base font-semibold">List of Brands</CardTitle>
                {brands.length > 0 && (
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                    <div className="relative">
                      <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                      <Input
                        data-testid="brands-search-input"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Search brand name…"
                        className="h-9 w-full pl-8 sm:w-56"
                      />
                    </div>
                    <Select value={sortBy} onValueChange={setSortBy}>
                      <SelectTrigger className="h-9 w-full sm:w-52" data-testid="brands-sort-select">
                        <ArrowUpDown className="mr-1.5 h-3.5 w-3.5 text-muted-foreground" />
                        <SelectValue placeholder="Sort by" />
                      </SelectTrigger>
                      <SelectContent>
                        {SORT_OPTIONS.map((opt) => (
                          <SelectItem key={opt.value} value={opt.value} data-testid={`brands-sort-${opt.value}`}>
                            {opt.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </CardHeader>
              <CardContent>
                {brands.length === 0 ? (
                  <div className="flex flex-col items-center justify-center rounded-md border border-dashed border-border py-12 text-center">
                    <Building2 className="h-8 w-8 text-muted-foreground" />
                    <p className="mt-3 text-sm font-medium">No brands yet</p>
                    <p className="mt-1 text-xs text-muted-foreground">
                      Add your first brand to start tracking traffic and balances.
                    </p>
                  </div>
                ) : visibleBrands.length === 0 ? (
                  <div className="flex flex-col items-center justify-center rounded-md border border-dashed border-border py-12 text-center" data-testid="brands-no-match">
                    <Search className="h-8 w-8 text-muted-foreground" />
                    <p className="mt-3 text-sm font-medium">No brands match “{search}”</p>
                    <p className="mt-1 text-xs text-muted-foreground">Try a different search term.</p>
                  </div>
                ) : (
                  <div className="w-full overflow-x-auto rounded-md border border-border">
                    <Table data-testid="brands-list-table">
                      <TableHeader>
                        <TableRow>
                          <TableHead>Brand</TableHead>
                          <TableHead className="text-right">Available Balance</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {visibleBrands.map((b, idx) => (
                          <TableRow
                            key={b.id}
                            data-testid={`brand-list-row-${idx}`}
                            onClick={() => navigate(`/brands/${b.id}`)}
                            className="cursor-pointer transition-colors duration-150 hover:bg-muted/60"
                          >
                            <TableCell className="font-medium">
                              <span className="flex items-center gap-2">
                                {b.name}
                                {b.week_confirmed && (
                                  <span
                                    data-testid={`brand-list-row-${idx}-confirmed-badge`}
                                    className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[11px] font-medium text-emerald-700"
                                  >
                                    <CheckCircle2 className="h-3 w-3" />
                                    Confirmed
                                  </span>
                                )}
                              </span>
                            </TableCell>
                            <TableCell
                              className={cn(
                                "text-right font-mono tabular-nums",
                                b.total < 0 ? "text-red-600" : "text-foreground"
                              )}
                            >
                              {fmtCurrency(b.total)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}

                <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                  <DialogTrigger asChild>
                    <Button className="mt-4" data-testid="brands-add-brand-button">
                      <Plus className="mr-1.5 h-4 w-4" />
                      Add Brand
                    </Button>
                  </DialogTrigger>
                  <DialogContent data-testid="add-brand-dialog">
                    <DialogHeader>
                      <DialogTitle>Add Brand</DialogTitle>
                      <DialogDescription>Create a new brand to track its traffic and balance.</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-1.5 py-2">
                      <Label htmlFor="brand-name">Brand name</Label>
                      <Input
                        id="brand-name"
                        data-testid="add-brand-name-input"
                        value={newName}
                        onChange={(e) => setNewName(e.target.value)}
                        placeholder="e.g. Brand A"
                        onKeyDown={(e) => e.key === "Enter" && createBrand()}
                        autoFocus
                      />
                    </div>
                    <DialogFooter>
                      <Button
                        onClick={createBrand}
                        disabled={creating || !newName.trim()}
                        data-testid="add-brand-submit-button"
                      >
                        {creating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                        Add Brand
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </CardContent>
            </Card>
          </>
        )}
      </main>
    </div>
  );
}
